/**
 * Morning Intelligence Capture · DEC-050.
 *
 * Single-page operational worksheet. The operator answers *"what changed
 * since yesterday?"* in ~2 minutes. Everything downstream — purchasing
 * signals, recommendations, the Morning Brief itself — refreshes when
 * they press the sticky **Prepare Morning Brief** button.
 *
 * Design contract (DEC-050):
 *   · one page, no scrolling between sections on 1080p typical entries
 *   · no skip toggles — empty inputs mean "nothing to report"
 *   · no visible Save Draft — silent auto-save on blur
 *   · one primary action at the bottom
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Coffee, Plus, Trash, CheckCircle, Circle, ArrowRight,
  Sun, CloudRain, Snowflake, Wind, Thermometer,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/* ------------------------------------------------------------------
   Constants — mirror backend SUPPORTED_PRODUCTS and WEATHER_CONDITIONS.
   ------------------------------------------------------------------ */
const PRODUCTS = [
  { code: "regular",       label: "Regular Gasoline" },
  { code: "premium",       label: "Premium Gasoline" },
  { code: "clear_diesel",  label: "Clear Diesel" },
  { code: "dyed_diesel",   label: "Dyed Diesel" },
  { code: "def",           label: "DEF" },
];

const WEATHER_OPTIONS = [
  { key: "clear",         label: "Clear",         icon: Sun },
  { key: "rain",          label: "Rain",          icon: CloudRain },
  { key: "snow",          label: "Snow",          icon: Snowflake },
  { key: "storm",         label: "Storm",         icon: Wind },
  { key: "extreme_cold",  label: "Extreme Cold",  icon: Thermometer },
];

const DIRECTION_OPTIONS_SUPPLIER = [
  { key: "supplier_constraint", label: "Constraint" },
  { key: "supports_buy",        label: "Supports Order" },
  { key: "against_buy",         label: "Against Order" },
];
const DIRECTION_OPTIONS_MARKET = [
  { key: "supports_buy", label: "Push higher / order more" },
  { key: "against_buy",  label: "Push lower / wait" },
];

/* ------------------------------------------------------------------
   Empty sections — mirror backend _empty_capture defaults so the UI
   works while today's document is being auto-created.
   ------------------------------------------------------------------ */
const EMPTY_SECTIONS = {
  customer_intel: { notes: "", entries: [] },
  supplier_intel: { notes: "", entries: [] },
  operations:     { notes: "" },
  fleet:          { has_issue: null, notes: "" },
  market:         { notes: "", entries: [] },
  weather:        { condition: null, notes: "" },
  general_notes:  { text: "" },
};

/* ------------------------------------------------------------------
   Debounced auto-save hook. Persists every 800 ms of idle after a
   change · never shows a spinner or toast · this is silent by design.
   ------------------------------------------------------------------ */
function useAutoSave(sections, enabled) {
  const timer = useRef(null);
  const inflight = useRef(false);
  const latest = useRef(sections);
  latest.current = sections;
  useEffect(() => {
    if (!enabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (inflight.current) return;
      inflight.current = true;
      try {
        await api.put("/morning-intelligence/today", { sections: latest.current });
      } catch {
        // Silent · a failing auto-save is not the operator's problem right
        // now — the sticky Prepare button will surface any error.
      } finally {
        inflight.current = false;
      }
    }, 800);
    return () => timer.current && clearTimeout(timer.current);
  }, [sections, enabled]);
}

/* ==================================================================
   Root
   ================================================================== */
export default function MorningCapture() {
  const { user } = useAuth();
  const canWrite = ["Administrator", "Owner", "Office Staff"].includes(user?.role);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [meta, setMeta] = useState({
    brief_date: "", status: "draft",
    prepared_by_name: "", prepared_at: null,
  });
  const [sections, setSections] = useState(EMPTY_SECTIONS);

  // Load today's draft on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/morning-intelligence/today");
        if (!alive) return;
        setSections(mergeSections(EMPTY_SECTIONS, data.sections));
        setMeta({
          brief_date:       data.brief_date,
          status:           data.status,
          prepared_by_name: data.prepared_by_name,
          prepared_at:      data.prepared_at,
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useAutoSave(sections, !loading && canWrite);

  const updateSection = useCallback((key, patch) => {
    setSections((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const prepareBrief = useCallback(async () => {
    if (!canWrite || preparing) return;
    setPreparing(true);
    try {
      const { data } = await api.post("/morning-intelligence/today/prepare", { sections });
      toast.success(`Morning Brief prepared · ${data.signals_materialised} signal${data.signals_materialised === 1 ? "" : "s"} generated.`);
      navigate(data.redirect_to || "/brief");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare Morning Brief.");
    } finally {
      setPreparing(false);
    }
  }, [canWrite, preparing, sections, navigate]);

  const briefDateLabel = useMemo(() => {
    if (!meta.brief_date) return "";
    try {
      const [y, m, d] = meta.brief_date.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: "long", month: "long", day: "numeric",
      });
    } catch { return meta.brief_date; }
  }, [meta.brief_date]);

  const entryCount = useMemo(() => {
    return (sections.customer_intel.entries?.length || 0)
         + (sections.supplier_intel.entries?.length || 0)
         + (sections.market.entries?.length || 0)
         + (sections.operations.notes ? 1 : 0)
         + (sections.fleet.has_issue ? 1 : 0)
         + (sections.weather.condition ? 1 : 0)
         + (sections.general_notes.text ? 1 : 0);
  }, [sections]);

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading today's intelligence…</div>;
  }

  return (
    <div className="pb-32" data-testid="mic-page">
      {/* Header ------------------------------------------------- */}
      <div className="border-b border-slate-200 pb-4 mb-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <Coffee size={22} weight="fill" className="text-amber-600" />
          <h1 className="text-2xl font-semibold text-slate-900">Morning Intelligence Capture</h1>
          <span className="text-sm text-slate-500" data-testid="mic-brief-date">· {briefDateLabel}</span>
          {meta.status === "prepared" && meta.prepared_at ? (
            <span data-testid="mic-status-prepared"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
              <CheckCircle size={11} weight="fill" /> Prepared · {new Date(meta.prepared_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          ) : (
            <span data-testid="mic-status-draft"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300">
              <Circle size={9} /> Draft
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-1.5" data-testid="mic-philosophy">
          Capture what changed since yesterday. Everything else is inferred automatically.
        </p>
      </div>

      {/* 7 prompt cards in a 2-column grid --------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CustomerIntelCard
          state={sections.customer_intel}
          onChange={(patch) => updateSection("customer_intel", patch)}
          disabled={!canWrite}
        />
        <SupplierIntelCard
          state={sections.supplier_intel}
          onChange={(patch) => updateSection("supplier_intel", patch)}
          disabled={!canWrite}
        />
        <OperationsCard
          state={sections.operations}
          onChange={(patch) => updateSection("operations", patch)}
          disabled={!canWrite}
        />
        <FleetCard
          state={sections.fleet}
          onChange={(patch) => updateSection("fleet", patch)}
          disabled={!canWrite}
        />
        <MarketIntelCard
          state={sections.market}
          onChange={(patch) => updateSection("market", patch)}
          disabled={!canWrite}
        />
        <WeatherCard
          state={sections.weather}
          onChange={(patch) => updateSection("weather", patch)}
          disabled={!canWrite}
        />
        <div className="lg:col-span-2">
          <GeneralNotesCard
            state={sections.general_notes}
            onChange={(patch) => updateSection("general_notes", patch)}
            disabled={!canWrite}
          />
        </div>
      </div>

      {/* Sticky footer ----------------------------------------- */}
      <div className="fixed bottom-0 inset-x-0 lg:left-64 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-6 py-3 flex items-center gap-4"
           data-testid="mic-footer">
        <div className="text-xs text-slate-600">
          <b data-testid="mic-entry-count">{entryCount}</b> section{entryCount === 1 ? "" : "s"} filled ·
          <span className="ml-1">auto-saving silently</span>
        </div>
        <button
          data-testid="mic-prepare"
          onClick={prepareBrief}
          disabled={!canWrite || preparing}
          className="ml-auto inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 shadow-sm"
        >
          {preparing ? "Preparing…" : "Prepare Morning Brief"}
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

/* ==================================================================
   Section helpers
   ================================================================== */
function mergeSections(base, incoming) {
  if (!incoming) return base;
  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(incoming || {}).map(([k, v]) => [k, { ...(base[k] || {}), ...(v || {}) }])
    ),
  };
}

function Card({ title, prompt, children, testId }) {
  return (
    <section data-testid={testId}
             className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <p className="text-xs text-slate-500 mt-0.5">{prompt}</p>
      </div>
      {children}
    </section>
  );
}

function TextArea({ value, onChange, placeholder, testId, rows = 2, disabled }) {
  return (
    <textarea
      data-testid={testId}
      value={value || ""}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:bg-slate-50 resize-y"
    />
  );
}

function ProductPicker({ value, onChange, testId, disabled }) {
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className="text-xs rounded-md border border-slate-300 px-2 py-1 bg-white"
    >
      <option value="">All products</option>
      {PRODUCTS.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
    </select>
  );
}

function DirectionPicker({ value, options, onChange, testId, disabled }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="text-xs rounded-md border border-slate-300 px-2 py-1 bg-white"
    >
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}

/* ------------------------------ Customer Intelligence ------------ */
function CustomerIntelCard({ state, onChange, disabled }) {
  const setEntries = (entries) => onChange({ entries });
  return (
    <Card testId="mic-section-customer"
          title="Customer Intelligence"
          prompt="Anything about customers the system should know today?">
      <ul className="space-y-2">
        {(state.entries || []).map((e, i) => (
          <li key={i} className="flex gap-2 items-start"
              data-testid={`mic-customer-entry-${i}`}>
            <input
              type="text"
              value={e.customer_name || ""}
              placeholder="Customer (optional)"
              disabled={disabled}
              onChange={(ev) => {
                const next = [...state.entries];
                next[i] = { ...next[i], customer_name: ev.target.value };
                setEntries(next);
              }}
              className="w-40 text-sm rounded-md border border-slate-300 px-2 py-1"
              data-testid={`mic-customer-name-${i}`}
            />
            <input
              type="text"
              value={e.note || ""}
              placeholder="What's happening?"
              disabled={disabled}
              onChange={(ev) => {
                const next = [...state.entries];
                next[i] = { ...next[i], note: ev.target.value };
                setEntries(next);
              }}
              className="flex-1 text-sm rounded-md border border-slate-300 px-2 py-1"
              data-testid={`mic-customer-note-${i}`}
            />
            <button
              onClick={() => setEntries(state.entries.filter((_, j) => j !== i))}
              disabled={disabled}
              className="p-1.5 text-slate-500 hover:text-rose-700 disabled:opacity-30"
              data-testid={`mic-customer-remove-${i}`}
            ><Trash size={14} /></button>
          </li>
        ))}
      </ul>
      <button
        onClick={() => setEntries([...(state.entries || []), { customer_name: "", note: "" }])}
        disabled={disabled}
        className="self-start inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900 disabled:opacity-30"
        data-testid="mic-customer-add"
      ><Plus size={12} weight="bold" /> Add customer</button>
    </Card>
  );
}

/* ------------------------------ Supplier Intelligence ------------ */
function SupplierIntelCard({ state, onChange, disabled }) {
  const setEntries = (entries) => onChange({ entries });
  return (
    <Card testId="mic-section-supplier"
          title="Supplier Intelligence"
          prompt="Anything affecting suppliers today?">
      <ul className="space-y-2">
        {(state.entries || []).map((e, i) => (
          <li key={i} className="space-y-1"
              data-testid={`mic-supplier-entry-${i}`}>
            <div className="flex gap-2">
              <input
                type="text"
                value={e.supplier || ""}
                placeholder="Supplier"
                disabled={disabled}
                onChange={(ev) => {
                  const next = [...state.entries];
                  next[i] = { ...next[i], supplier: ev.target.value };
                  setEntries(next);
                }}
                className="w-32 text-sm rounded-md border border-slate-300 px-2 py-1"
                data-testid={`mic-supplier-name-${i}`}
              />
              <ProductPicker
                value={e.product_code}
                testId={`mic-supplier-product-${i}`}
                disabled={disabled}
                onChange={(pc) => {
                  const next = [...state.entries];
                  next[i] = { ...next[i], product_code: pc };
                  setEntries(next);
                }}
              />
              <DirectionPicker
                value={e.direction || "supplier_constraint"}
                options={DIRECTION_OPTIONS_SUPPLIER}
                testId={`mic-supplier-direction-${i}`}
                disabled={disabled}
                onChange={(d) => {
                  const next = [...state.entries];
                  next[i] = { ...next[i], direction: d };
                  setEntries(next);
                }}
              />
              <button
                onClick={() => setEntries(state.entries.filter((_, j) => j !== i))}
                disabled={disabled}
                className="p-1.5 text-slate-500 hover:text-rose-700 disabled:opacity-30"
                data-testid={`mic-supplier-remove-${i}`}
              ><Trash size={14} /></button>
            </div>
            <input
              type="text"
              value={e.note || ""}
              placeholder="What's happening?"
              disabled={disabled}
              onChange={(ev) => {
                const next = [...state.entries];
                next[i] = { ...next[i], note: ev.target.value };
                setEntries(next);
              }}
              className="w-full text-sm rounded-md border border-slate-300 px-2 py-1"
              data-testid={`mic-supplier-note-${i}`}
            />
          </li>
        ))}
      </ul>
      <button
        onClick={() => setEntries([...(state.entries || []), { supplier: "", product_code: null, note: "", direction: "supplier_constraint" }])}
        disabled={disabled}
        className="self-start inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900 disabled:opacity-30"
        data-testid="mic-supplier-add"
      ><Plus size={12} weight="bold" /> Add supplier note</button>
    </Card>
  );
}

/* ------------------------------ Operations ---------------------- */
function OperationsCard({ state, onChange, disabled }) {
  return (
    <Card testId="mic-section-operations"
          title="Operations"
          prompt="Anything affecting today's operations?">
      <TextArea
        value={state.notes}
        onChange={(v) => onChange({ notes: v })}
        placeholder="Team briefing, planned downtime, meetings, priorities…"
        testId="mic-operations-notes"
        disabled={disabled}
        rows={3}
      />
    </Card>
  );
}

/* ------------------------------ Fleet --------------------------- */
function FleetCard({ state, onChange, disabled }) {
  return (
    <Card testId="mic-section-fleet"
          title="Fleet"
          prompt="Any fleet or staffing issues today?">
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="mic-fleet"
            data-testid="mic-fleet-none"
            checked={state.has_issue === false || state.has_issue === null}
            onChange={() => onChange({ has_issue: false })}
            disabled={disabled}
          />
          None
        </label>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="mic-fleet"
            data-testid="mic-fleet-yes"
            checked={state.has_issue === true}
            onChange={() => onChange({ has_issue: true })}
            disabled={disabled}
          />
          Yes
        </label>
      </div>
      {state.has_issue === true && (
        <TextArea
          value={state.notes}
          onChange={(v) => onChange({ notes: v })}
          placeholder="Which truck / driver / issue?"
          testId="mic-fleet-notes"
          disabled={disabled}
        />
      )}
    </Card>
  );
}

/* ------------------------------ Market Intelligence -------------- */
function MarketIntelCard({ state, onChange, disabled }) {
  const setEntries = (entries) => onChange({ entries });
  return (
    <Card testId="mic-section-market"
          title="Market Intelligence"
          prompt="Have you heard anything affecting fuel markets today?">
      <ul className="space-y-2">
        {(state.entries || []).map((e, i) => (
          <li key={i} className="space-y-1"
              data-testid={`mic-market-entry-${i}`}>
            <div className="flex gap-2">
              <ProductPicker
                value={e.product_code}
                testId={`mic-market-product-${i}`}
                disabled={disabled}
                onChange={(pc) => {
                  const next = [...state.entries];
                  next[i] = { ...next[i], product_code: pc };
                  setEntries(next);
                }}
              />
              <DirectionPicker
                value={e.direction || "supports_buy"}
                options={DIRECTION_OPTIONS_MARKET}
                testId={`mic-market-direction-${i}`}
                disabled={disabled}
                onChange={(d) => {
                  const next = [...state.entries];
                  next[i] = { ...next[i], direction: d };
                  setEntries(next);
                }}
              />
              <button
                onClick={() => setEntries(state.entries.filter((_, j) => j !== i))}
                disabled={disabled}
                className="p-1.5 text-slate-500 hover:text-rose-700 disabled:opacity-30 ml-auto"
                data-testid={`mic-market-remove-${i}`}
              ><Trash size={14} /></button>
            </div>
            <input
              type="text"
              value={e.note || ""}
              placeholder="What did you hear?"
              disabled={disabled}
              onChange={(ev) => {
                const next = [...state.entries];
                next[i] = { ...next[i], note: ev.target.value };
                setEntries(next);
              }}
              className="w-full text-sm rounded-md border border-slate-300 px-2 py-1"
              data-testid={`mic-market-note-${i}`}
            />
          </li>
        ))}
      </ul>
      <button
        onClick={() => setEntries([...(state.entries || []), { product_code: null, note: "", direction: "supports_buy" }])}
        disabled={disabled}
        className="self-start inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900 disabled:opacity-30"
        data-testid="mic-market-add"
      ><Plus size={12} weight="bold" /> Add market note</button>
    </Card>
  );
}

/* ------------------------------ Weather ------------------------ */
function WeatherCard({ state, onChange, disabled }) {
  return (
    <Card testId="mic-section-weather"
          title="Weather & External Factors"
          prompt="Any weather or road conditions worth noting?">
      <div className="flex flex-wrap gap-2">
        {WEATHER_OPTIONS.map(({ key, label, icon: Icon }) => {
          const active = state.condition === key;
          return (
            <button
              key={key}
              onClick={() => onChange({ condition: active ? null : key })}
              disabled={disabled}
              data-testid={`mic-weather-${key}`}
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${active
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"} disabled:opacity-40`}
            >
              <Icon size={12} weight={active ? "fill" : "regular"} />
              {label}
            </button>
          );
        })}
      </div>
      {state.condition && state.condition !== "clear" && (
        <TextArea
          value={state.notes}
          onChange={(v) => onChange({ notes: v })}
          placeholder="Additional detail (optional)"
          testId="mic-weather-notes"
          disabled={disabled}
        />
      )}
    </Card>
  );
}

/* ------------------------------ General Notes ------------------- */
function GeneralNotesCard({ state, onChange, disabled }) {
  return (
    <Card testId="mic-section-general"
          title="General Notes"
          prompt="Anything else the team should know today?">
      <TextArea
        value={state.text}
        onChange={(v) => onChange({ text: v })}
        placeholder="Free-form notes…"
        testId="mic-general-text"
        disabled={disabled}
        rows={3}
      />
    </Card>
  );
}
